import type { ReactNode } from "react";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";

/**
 * The shared pieces of the top nav bar: which paths light up which trigger,
 * the styling of a nav item, and the two things a nav item can be — a direct
 * link or a dropdown trigger.
 *
 * These live apart from the bar itself because the overflow menu needs the same
 * items and the same active-path rules; duplicating either would let the inline
 * nav and the "More" menu drift apart.
 */

export interface TopNavBarProps {
  /** Current route — drives the active-item highlight. */
  activePath: string;
  onNavigate: (path: string) => void;
  onHelpCenter: () => void;
  /** Drafts link only shows when the company has draft shipments. */
  showDrafts: boolean;
  /**
   * The Developers group only shows when the company has a webhook, or delivery
   * history from one it deleted (still inside retention). Its first
   * entry is Webhook Deliveries; API access and the webhooks themselves join
   * it when they become self-service.
   */
  showDevelopers: boolean;
  /**
   * Whether the account menu offers "Pair Mobile Device". Employees /
   * non-production only — the iOS app hasn't shipped to customers yet
   * (ENG-6029). See showPairMobileDevice.
   */
  showPairMobile: boolean;
  /** Get-quotes CTA hides during the booking flow. */
  showGetQuotes: boolean;
  onGetQuotes: () => void;
  onReferralCode: () => void;

  userName: string;
  companyName: string;
  /** The Cargo CurrencyTag, passed in so this stays decoupled from the API types. */
  currencyTag?: ReactNode;

  coupon?: ReactNode;
  invoiceWarning?: ReactNode;
  /** "Demo" environment label shown beside the logo. */
  demoLabel?: ReactNode;
  /** Employee-only dev menu section, rendered between Switch Org and Sign Out. */
  devMenu?: ReactNode;
  /** Ghost search — hidden until we actually support lookup. */
  showSearch?: boolean;
}

/** Paths that light up each dropdown trigger. */
export const SHIPMENTS_PATHS = [
  "/",
  "/quotes",
  "/drafts",
  "/view-all-shipments",
];
export const MANAGE_COMPANY_PATHS = [
  "/address-book",
  "/brokers",
  "/saved-commodities",
  "/view-company",
];
export const FINANCIAL_PATHS = [
  "/credit-cards",
  "/bank-accounts",
  "/view-invoices",
  "/payments",
];
export const DEVELOPERS_PATHS = ["/webhook-deliveries"];

export interface NavEntry {
  key: string;
  kind: "link" | "menu" | "external";
  title: string;
  path?: string | undefined;
  activePaths?: string[] | undefined;
}

// Matches the gap-1 (0.25rem) between inline nav items.
export const NAV_GAP = 4;

const itemBase =
  "rounded-md px-2 py-1 text-xs whitespace-nowrap transition-colors";

function itemClasses(active: boolean): string {
  return active
    ? `${itemBase} bg-brand-subtle font-semibold text-brand-text`
    : `${itemBase} font-medium text-content-secondary hover:bg-muted hover:text-foreground`;
}

/** A direct nav link (Active Shipments, Recent Quotes, …). */
export function NavBarItem({
  title,
  active,
  onClick,
  testId,
}: {
  title: string;
  active: boolean;
  onClick: () => void;
  testId: string | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={itemClasses(active)}
    >
      {title}
    </button>
  );
}

/** The trigger for a dropdown nav group (Manage Company, Financial). */
export function NavBarMenuTrigger({
  title,
  active,
  testId,
}: {
  title: string;
  active: boolean;
  testId: string | undefined;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      className={`inline-flex items-center gap-1 ${itemClasses(active)}`}
    >
      {title}
      <CaretDownIcon className="h-3.5 w-3.5 opacity-60" weight="bold" />
    </button>
  );
}
